/**
 * Plausibility limits for the physical health measurements a parent or carer
 * enters (physicalHealthInfoGroup). They catch impossible entries, such as a
 * wrong unit or a missing digit, without ever refusing a real school-age child
 * (4 to 19 years):
 *
 * - Weight, height and head circumference: each low sits below the CDC
 *   biologically implausible value cut-off (z = -5) for a 4-year-old on the
 *   UK-WHO and CDC growth references, and each high above the largest values
 *   reported for adolescents, including skeletal dysplasia, severe obesity
 *   and macrocephaly.
 * - Pulse and blood pressure: wide margins around the APLS paediatric resting
 *   vital sign ranges and the NHLBI Fourth Report blood pressure tables, so an
 *   unwell child's real reading is still accepted.
 *
 * The server holds the same limits (rms_validation.py); change both together.
 * A value is checked only when one is entered: "unable to take this
 * measurement" and "not sure" are always fine.
 */
export const MEASUREMENT_LIMITS = {
  weight: { kg: [8, 250], lb: [18, 550] },
  height: { cm: [70, 220], inches: [28, 86] },
  headCircumference: { cm: [35, 70], in: [14, 27.5] },
  pulse: [30, 220],
  systolic: [50, 250],
  diastolic: [20, 150],
};

function num(v) {
  if (v === null || v === undefined || v === '') return null;
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
}

const within = (n, [lo, hi]) => n >= lo && n <= hi;

/**
 * What is wrong with an entered measurement, keyed by the field it belongs to
 * ('weight', 'height', 'value', 'systolic', 'diastolic'). Empty when nothing
 * entered is out of range.
 */
export function measurementProblems(measurementType, value) {
  const v = value && typeof value === 'object' ? value : {};
  const problems = {};

  if (measurementType === 'height_weight') {
    const w = v.weight || {};
    const weight = num(w.value);
    const wUnit = w.unit === 'lb' ? 'lb' : 'kg';
    const wLimits = MEASUREMENT_LIMITS.weight[wUnit];
    if (weight !== null && !within(weight, wLimits)) {
      problems.weight = `Enter a weight between ${wLimits[0]} and ${wLimits[1]} ${wUnit}`;
    }
    const h = v.height || {};
    if (h.unit === 'ft_in') {
      const ft = num(h.ft);
      const inches = num(h.in);
      if (ft !== null || inches !== null) {
        const total = (ft || 0) * 12 + (inches || 0);
        if (!within(total, MEASUREMENT_LIMITS.height.inches)) {
          problems.height = 'Enter a height between 2 ft 4 in and 7 ft 2 in';
        }
      }
    } else {
      const height = num(h.value);
      const [lo, hi] = MEASUREMENT_LIMITS.height.cm;
      if (height !== null && !within(height, [lo, hi])) {
        problems.height = `Enter a height between ${lo} and ${hi} cm`;
      }
    }
    return problems;
  }

  if (measurementType === 'head_circumference') {
    if (v.notSure === true) return problems;
    const unit = v.unit === 'in' ? 'in' : 'cm';
    const limits = MEASUREMENT_LIMITS.headCircumference[unit];
    const n = num(v.value);
    if (n !== null && !within(n, limits)) {
      problems.value = `Enter a head circumference between ${limits[0]} and ${limits[1]} ${unit === 'in' ? 'inches' : 'cm'}`;
    }
    return problems;
  }

  if (measurementType === 'pulse_rate') {
    if (v.unable === true) return problems;
    const n = num(v.value);
    const [lo, hi] = MEASUREMENT_LIMITS.pulse;
    if (n !== null && !within(n, [lo, hi])) {
      problems.value = `Enter a pulse rate between ${lo} and ${hi} beats per minute`;
    }
    return problems;
  }

  if (measurementType === 'blood_pressure') {
    if (v.unable === true) return problems;
    const sys = num(v.systolic);
    const dia = num(v.diastolic);
    const [sLo, sHi] = MEASUREMENT_LIMITS.systolic;
    const [dLo, dHi] = MEASUREMENT_LIMITS.diastolic;
    if (sys !== null && !within(sys, [sLo, sHi])) {
      problems.systolic = `Enter a top number between ${sLo} and ${sHi}`;
    }
    if (dia !== null && !within(dia, [dLo, dHi])) {
      problems.diastolic = `Enter a bottom number between ${dLo} and ${dHi}`;
    }
    if (!problems.systolic && !problems.diastolic && sys !== null && dia !== null && sys <= dia) {
      problems.diastolic = 'The bottom number should be lower than the top number';
    }
    return problems;
  }

  return problems;
}

export function hasMeasurementProblem(measurementType, value) {
  return Object.keys(measurementProblems(measurementType, value)).length > 0;
}
